import ExpandMore from '@mui/icons-material/ExpandMore'
import SearchIcon from '@mui/icons-material/Search'
import { Collapse, Skeleton, TextField, InputAdornment } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { getFaq } from 'src/consumer/services/getFaq'
import { MUIThemeConfig } from 'src/context/MUIThemeConfig'
import { useScreenSize } from 'src/customHooks/customHooks'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import { formFAQDataWithRespectiveFAQCategories } from '../../../faq_utils'
import FAQItem from '../../common/FAQItem'
import { FAQ_LAYOUTS } from '../../utils'
import FAQCategoryContainerWrapper from './FAQCategoryContainerWrapper'
import FAQItemsContainerWrapper from './FAQItemsContainerWrapper'
import {
  FAQCategoriesContainerWithDropDownButton,
  FAQCategoryItem,
  FAQContentCard,
  FAQEmptyState,
  FAQHeader,
  FAQSidebarLayoutContainer,
  FAQSidebarLayoutFAQsContainer,
} from './styledComponent'

export default function SidebarLayout({ faqCategories, loadingCategories, layoutType }) {
  const [faqData, setFaqData] = useState()
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [openFAQData, setOpenFAQData] = useState({
    index: -1,
    categoryId: null,
  })

  const [expandCategoriesSection, setExpandCategoriesSection] = useState(false)

  const { width, height } = useScreenSize()

  const isMobile = width <= MUIThemeConfig.breakpoints.values.md

  const selectedFaqCategory = faqData?.find((faqData) => faqData?.id === openFAQData?.categoryId)

  const normalizedFaqCategories = useMemo(() => {
    return faqCategories?.map((cat) => ({ ...cat, faqs: cat?.faqs || [] })) || []
  }, [faqCategories])

  function selectFaqCategory(category) {
    if (!category) return
    getFAQsBasedOnSelectedFaqCategory(category)
    setOpenFAQData((prev) => ({ ...prev, categoryId: category?.id, index: 0 }))
    setSearchTerm('')
    setExpandCategoriesSection(false)
  }

  function handleExpandCategory() {
    setExpandCategoriesSection((prev) => !prev)
  }

  function openFaqItem(index) {
    setOpenFAQData((prev) => ({ ...prev, index }))
  }

  async function getFAQsBasedOnSelectedFaqCategory(categoryToFetch) {
    setLoading(true)
    // If FAQs are already present, use them without hitting the API.
    if (categoryToFetch?.faqs?.length) {
      setFaqData((prev) =>
        prev?.map((faqCat) => (faqCat?.id === categoryToFetch?.id ? categoryToFetch : faqCat)),
      )
      setLoading(false)
      return
    }

    let searchString = ''
    if (categoryToFetch) {
      searchString = categoryToFetch?.id
    }
    if (searchString) {
      let response = await getFaq(searchString, { withCategoryDetails: false, size: 999 })
      if (response?.status === 200 && isCommonResponseSuccessful(response.data.code)) {
        const faqData = response?.data?.data?.faqPage?.content
        const faqCategoriesData = faqCategories
        const formedFaqData = formFAQDataWithRespectiveFAQCategories(faqData, faqCategoriesData)

        const faqCategoryUpdated = formedFaqData?.find(
          (faqCat) => faqCat?.id === categoryToFetch?.id,
        )

        setFaqData((prev) =>
          prev?.map((faqCat) => (faqCat?.id === categoryToFetch?.id ? faqCategoryUpdated : faqCat)),
        )
        setOpenFAQData((prev) => ({ ...prev, index: 0 }))
      } else {
        console.log('Error getting FaqData')
      }
    }
  }

  useEffect(() => {
    // Prime local state with whatever FAQs were already provided.
    setFaqData(normalizedFaqCategories)
  }, [normalizedFaqCategories])

  useEffect(() => {
    if (faqData && openFAQData?.categoryId === null) {
      selectFaqCategory(normalizedFaqCategories?.[0])
    }
  }, [faqData, normalizedFaqCategories, openFAQData?.categoryId])

  const filteredFaqs = useMemo(() => {
    if (!selectedFaqCategory?.faqs) return []
    const term = searchTerm.trim().toLowerCase()
    if (!term) return selectedFaqCategory?.faqs
    return selectedFaqCategory?.faqs?.filter((faq) => {
      const question = faq?.question?.toLowerCase() || ''
      const answer = faq?.answer?.toLowerCase() || ''
      return question.includes(term) || answer.includes(term)
    })
  }, [searchTerm, selectedFaqCategory])

  return (
    <FAQSidebarLayoutContainer>
      <FAQHeader>
        <div>
          <div className="title">FAQs</div>
          <div className="subtext">
            {faqData?.length || 0} categories • {selectedFaqCategory?.faqs?.length || 0} articles
          </div>
        </div>
        <TextField
          size="small"
          placeholder="Search FAQs"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </FAQHeader>
      <FAQContentCard>
        <FAQSidebarLayoutFAQsContainer>
          <FAQCategoriesContainerWithDropDownButton>
            {!!isMobile && (
              <FAQCategoryItem
                disableRipple
                className="d-flex align-items-center justify-content-between"
                onClick={handleExpandCategory}
                style={{ borderBottom: 'unset' }}
              >
                <h2 className="category-name">{selectedFaqCategory?.title}</h2>
                <ExpandMore
                  sx={{
                    transform: expandCategoriesSection ? 'rotate(180deg)' : 'unset',
                    transition: 'all 150ms ease-in-out',
                  }}
                />
              </FAQCategoryItem>
            )}
            <Collapse in={!isMobile ? true : expandCategoriesSection}>
              <FAQCategoryContainerWrapper layoutType={layoutType}>
                {faqData?.map((category) => (
                  <FAQCategoryItem
                    selected={selectedFaqCategory?.id === category?.id}
                    key={category?.id}
                    onClick={() => selectFaqCategory(category)}
                  >
                    <div className="category-name">{category?.title}</div>
                    <span className="category-count">{category?.faqs?.length || 0}</span>
                  </FAQCategoryItem>
                ))}
              </FAQCategoryContainerWrapper>
            </Collapse>
          </FAQCategoriesContainerWithDropDownButton>
          <FAQItemsContainerWrapper layoutType={layoutType}>
            {filteredFaqs?.length > 0 ? (
              filteredFaqs?.map((faq, idx) => (
                <FAQItemViewController
                  key={faq?.id || idx}
                  item={faq}
                  itemIndex={idx}
                  handleOpenFAQ={openFaqItem}
                  isExpanded={idx === openFAQData?.index}
                  layoutType={layoutType}
                  screenWidth={width}
                />
              ))
            ) : (
              <FAQEmptyState>
                {loading ? (
                  <>
                    <ExpandedFAQSkeleton />
                    <CollapsedFAQSkeleton layoutType={layoutType} screenWidth={width} />
                    <CollapsedFAQSkeleton layoutType={layoutType} screenWidth={width} />
                  </>
                ) : (
                  <p>No FAQs match your search.</p>
                )}
              </FAQEmptyState>
            )}
          </FAQItemsContainerWrapper>
        </FAQSidebarLayoutFAQsContainer>
      </FAQContentCard>
    </FAQSidebarLayoutContainer>
  )
}

function FAQItemViewController({
  item,
  itemIndex,
  handleOpenFAQ,
  isExpanded,
  layoutType,
  screenWidth,
}) {
  const isPC = screenWidth > MUIThemeConfig.breakpoints.values.xl

  switch (layoutType) {
    case FAQ_LAYOUTS.TWO_COLUMN_SIDEBAR:
      return (
        <FAQItem
          item={item}
          itemIndex={itemIndex}
          handleOpenFAQ={handleOpenFAQ}
          isExpanded={isPC ? true : isExpanded}
          hideExpandIcon={isPC}
          lockExpand={isPC}
        />
      )
    default:
      return (
        <FAQItem
          item={item}
          itemIndex={itemIndex}
          handleOpenFAQ={handleOpenFAQ}
          isExpanded={isExpanded}
        />
      )
  }
}

function ExpandedFAQSkeleton() {
  return (
    <div className="w-100 d-flex justify-content-center ">
      <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: '10px' }} />
    </div>
  )
}

function CollapsedFAQSkeleton({ layoutType, screenWidth }) {
  const isPC = screenWidth > MUIThemeConfig.breakpoints.values.xl

  function defaultRender() {
    return (
      <div className="w-100 d-flex justify-content-center ">
        <Skeleton variant="rectangular" width="100%" height={50} sx={{ borderRadius: '10px' }} />
      </div>
    )
  }

  switch (layoutType) {
    case FAQ_LAYOUTS.TWO_COLUMN_SIDEBAR:
      return isPC ? <ExpandedFAQSkeleton /> : defaultRender()
    default:
      return defaultRender()
  }
}
